// Imports
import {
	Directive,
	Input,
	OnInit,
	HostListener,
	ElementRef,
	EventEmitter,
	Output
} from '@angular/core';
import {NgModel} from '@angular/forms';

/** Summary
 * 	The MaskDirective allows you to add the tag "mask=..." to an input tag to mask the typed input.
 * 	It is compatible with [(ngModel)] and will update the model accordingly.
 *  IT IS NOT COMPATIBLE WITH [(value)] AND IS THEREBY DEPRECIATED
 * 	@Input mask is a string of characters. MaskDirective creates a regular expression for it as follows:
 * 	    [...] indicates that the characters within the brackets will be treated as a block
 * 	        eg: [(999)]? means that the (999) can be omitted, but must be matched entirely if it is included
 * 		0-9 is any number
 * 		a-z is any letter
 * 		? means the preceding character is optional
 *		+ means one or more of the preceding character
 *		* means zero or more of the preceding character
 *		any other character is literally that character
 *	@WARNING This class must be custom-made to work with objects in the emitNewValue function. Currently works with:
 *		Date
 */
@Directive ({
    selector: '[mask]'
})
export class MaskDirective implements OnInit {

    // Properties
    @Input('mask') mask: string;
    @Input() startingValue: string;
    @Output() ngModelChange: EventEmitter<any> = new EventEmitter();
    oldValue: string;
    regExp: RegExp;
    allowableCharacters: RegExp[] = [];
    specialCharacters: string[] = [];
    maxLength = 0;
    performedMask = false;

    // Class Functions
    constructor (
        private element: ElementRef,
		private ngModel: NgModel
    ) {}

    // Event Functions
    ngOnInit () {
        // POST: constructs a regular expression from the input value, mask, while also determining special characters
        // NOTE: always adds found characters to allowable characters

        // Constructing regular expression
        let regExpString = '^';
        for (let i = 0; i < this.mask.length; i++) {

            // Skipping count symbols
            if (this.mask.charAt(i) == '?' || this.mask.charAt(i) == '*' || this.mask.charAt(i) == '+') {
                continue;
            }

            // Adding optional values
            const optional: boolean = this.mask.length > i + 1 && this.mask.charAt(i + 1) == '?';
            const anyNumber: boolean = this.mask.length > i + 1 && this.mask.charAt(i + 1) == '*';
            const oneOrMore: boolean = this.mask.length > i + 1 && this.mask.charAt(i + 1) == '+';

			// Adjusting for parenthetical
			if (this.mask.charAt(i) == '[') {
				regExpString += '((';
				continue;
			}
			if (this.mask.charAt(i) == ']') {
                regExpString += ')';
				regExpString += optional ? '?' : '';
				regExpString += anyNumber ? '*' : '';
				regExpString += oneOrMore ? '+' : '';
				regExpString += '|$)';
				continue;
            }

            // Adding opening parenthetical
			regExpString += '(';

			// Add numbers
            if (this.mask.charAt(i).match(/\d/)) {
                regExpString += '\\d';
                if (!this.allowableCharacters.includes(/\d/)) {
                    this.allowableCharacters.push(/\d/);
                }
            } else if (this.mask.charAt(i).match(/[A-Za-z]/)) {
                regExpString += '[A-Za-z]';
                if (!this.allowableCharacters.includes(/[A-Za-z]/)) {
                    this.allowableCharacters.push(/[A-Za-z]/);
                }
            } else {

                // Checking for regexp important characters
                switch (this.mask.charAt(i)) {
                    case '(':
                        regExpString += '\\(';
                        break;
                    case ')':
                        regExpString += '\\)';
                        break;
                    case '.':
                        regExpString += '\\.';
                        break;
                    case '$':
                        regExpString += '\\$';
                        break;
                    case '^':
                        regExpString += '\\^';
                        break;
                    case '\\':
                        regExpString += '\\\\';
                        break;
                    default:
                        regExpString += this.mask.charAt(i);
                }

                // Adding a special character if one was found
                if (!this.specialCharacters.includes(this.mask.charAt(i))) {
                    this.specialCharacters.push(this.mask.charAt(i));
                }
                const allowedRegEx = new RegExp('\\' + this.mask.charAt(i));
                if (!this.allowableCharacters.includes(allowedRegEx)) {
                    this.allowableCharacters.push(allowedRegEx);
                }
            }

            // Adding in optionals, end of string, and closing parenthetical
			regExpString += optional ? '?' : '';
			regExpString += anyNumber ? '*' : '';
			regExpString += oneOrMore ? '+' : '';
			regExpString += '|$)';

            // Adjusting max length
            if (oneOrMore || anyNumber) {
                this.maxLength = Infinity;
            } else if (this.maxLength != Infinity) {
                this.maxLength += 1;
            }
        }

        // Making the old value the starting value for first input
		this.oldValue = this.startingValue;

		// Finalizing regExp
        regExpString += '$';
        this.regExp = new RegExp(regExpString);
    }

    @HostListener('blur')
	saveToObjectModel () {
    	this.emitNewValue();
	}

    @HostListener('input', ['$event'])
    performMask (event) {
        // PRE: an event from element it is on
        // POST: performs the mask
        const currentValue: string = this.element.nativeElement.value;
        let newValue: string = currentValue;
        this.performedMask = true;

        // We don't want to perform input events if we're capturing ngModelChange
        if (!event || !event.data || !this.regExp) {
            this.oldValue = currentValue;
            return;
        }
        // Not implementing change if not allowable character or overflows
        let allowedCharacter = false;
        this.allowableCharacters.forEach (allowableCharacter => {
            allowedCharacter = event.data.match(allowableCharacter) != null || event.data == allowableCharacter || allowedCharacter;
        });
        if ((!allowedCharacter && this.oldValue != null) || this.element.nativeElement.value.length > this.maxLength) {
            this.element.nativeElement.value = this.oldValue;
			if (this.ngModel == null || typeof(this.ngModel.value) != 'object') {
				this.emitNewValue();
			}
            return;
        }

        // If mask doesn't work, try to construct a new string
        if (!currentValue.match(this.regExp)) {
            let constructedValue = '';
            for (let i = 0; i < currentValue.length; i++) {
                let testValue = constructedValue + currentValue.charAt(i);

                // If the string at this point doesn't work, test aganist special characters
                if (!testValue.match(this.regExp)) {
                    for (let j = 0; j < this.specialCharacters.length; j++) {
                        testValue = constructedValue + this.specialCharacters[j];

                        // If a special character makes it work, add that in and exit
                        if (testValue.match(this.regExp)) {
                            constructedValue = testValue;
                            i--;
                            break;
                        }
                    }
                } else {
                    constructedValue = testValue;
                }
            }

            // If our new value works, use it
            if (constructedValue.match(this.regExp)) {
                newValue = constructedValue;
            } else if (this.oldValue != null) {
                newValue = this.oldValue;
            }
        }

        // Making our new element the old value
        this.oldValue = newValue;
        this.element.nativeElement.value = this.oldValue;
        if (this.ngModel == null || typeof(this.ngModel.value) != 'object') {
			this.emitNewValue();
		}
    }

    emitNewValue () {
		// PRE: recently updated oldValue
        // POST: emits the oldValue as a newValue

        // Don't emit if no mask was performed
        if (!this.performedMask) {
            return;
        }

        // Emit the old value so long as we have an ngModel and it's not an object
		if (this.ngModel == null || typeof(this.ngModel.value) != 'object') {
			this.ngModelChange.emit(this.oldValue);
        }

        // If it is an object, check if the value exists and emit the proper type
        else if (this.ngModel.value != null) {
			switch (this.toType(this.ngModel.value)) {
				case 'date':
					this.ngModelChange.emit(new Date(this.oldValue));
			}
		}
	}

    toType (object): string {
    	// PRE: An object must be supplied. Anything else WILL break your code.
		// POST: returns the name of the object in lower case
    	return object.__proto__.constructor.name.toLowerCase();
	}
}
